import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';

import {
  getLANLWeatherTowersQuery,
  getLatestDataQuery,
} from '../../utils/getWeatherData';
import {
  celsiusToFahrenheit,
  meterPerSecondToMPH,
  windDirection,
} from '../../utils/conversions';

import './CurrentConditionsBanner.scss';
import { Select } from '../Select';

const defaultCurrentConditions = [
  { label: 'Temp (ºF)', value: '-' },
  { label: 'Wind (mph)', value: '-' },
  { label: 'Dew Point (ºF)', value: '-' },
  { label: 'RH (%)', value: '-' },
];

const STORAGE_KEY = 'selectedTower';

export const CurrentConditionsBanner = ({ className }) => {
  const classes = classNames('current-conditions-banner', className);

  const [currentConditions, setCurrentConditions] = useState(
    defaultCurrentConditions,
  );
  const [lastUpdated, setLastUpdated] = useState('-');
  const [towerList, setTowerList] = useState([]);
  const [stationData, setStationData] = useState(null);
  const [towerWeatherData, setTowerWeatherData] = useState({});
  const [isSwitching, setIsSwitching] = useState(false);

  // Fetch stations on mount and load saved selection from localStorage
  useEffect(() => {
    const fetchStations = async () => {
      try {
        const stations = await getLANLWeatherTowersQuery(['A'], false, [
          'ncom',
          'laarc',
          'wrarc',
        ]);
        setTowerList(stations);

        // Retrieve saved station value from localStorage
        const savedStationValue = localStorage.getItem(STORAGE_KEY);
        const defaultStation =
          stations.find(({ value }) => value === savedStationValue) ||
          stations.find(({ value }) => value === 'ta6');
        setStationData(defaultStation);
      } catch (error) {
        console.error('Error fetching stations:', error);
      }
    };
    fetchStations();
  }, []);

  // Preload weather data for all towers when towerList is available
  useEffect(() => {
    if (towerList.length > 0) {
      const fetchAllTowerData = async () => {
        try {
          const newWeatherData = {};
          await Promise.all(
            towerList.map(async (tower) => {
              const view = tower.views.find((view) => view.includes('15_vw'));
              if (view) {
                const data = await getLatestDataQuery(view);
                const cleanDate = data?.datetime?.replace("'", '') || '';
                const lastUpdatedDate = moment.tz(
                  cleanDate,
                  'YYYY-MM-DD hh:mm:sss',
                  'America/Denver',
                );
                const conditions = [
                  {
                    label: 'Temp (ºF)',
                    value: `${data?.temp0 ? celsiusToFahrenheit(Number(data?.temp0)) : '-'}`,
                  },
                  {
                    label: 'Wind (mph)',
                    value: `${data?.spd1 ? meterPerSecondToMPH(data?.spd1) : '-'} from ${data?.dir1 ? windDirection(data?.dir1) : '-'}`,
                  },
                  {
                    label: 'Dew Point (ºF)',
                    value: `${data?.dewp ? celsiusToFahrenheit(Number(data.dewp)) : '-'}`,
                  },
                  { label: 'RH (%)', value: `${data?.rh ?? '-'}` },
                ];
                const lastUpdatedString = `${lastUpdatedDate.format('MMMM D, YYYY')} at ${lastUpdatedDate.format('h:mma')} MST`;
                newWeatherData[tower.value] = {
                  conditions,
                  lastUpdated: lastUpdatedString,
                };
              }
            }),
          );
          setTowerWeatherData(newWeatherData);
        } catch (error) {
          console.error('Error fetching tower weather data:', error);
        }
      };

      fetchAllTowerData();
      const intervalId = setInterval(fetchAllTowerData, 60000);
      return () => clearInterval(intervalId);
    }
  }, [towerList]);

  // On initial load, update display if data is ready and no tower switch is occurring
  useEffect(() => {
    if (
      !isSwitching &&
      stationData &&
      towerWeatherData[stationData.value] &&
      lastUpdated === '-'
    ) {
      setCurrentConditions(towerWeatherData[stationData.value].conditions);
      setLastUpdated(towerWeatherData[stationData.value].lastUpdated);
    }
  }, [towerWeatherData, stationData, isSwitching, lastUpdated]);

  // Handle station selection: show default values briefly, then update display
  const handleChange = (e) => {
    const selectedValue = e.target.value;
    const selectedStation = towerList.find(
      ({ value }) => value === selectedValue,
    );
    if (selectedStation) {
      // Save the selection in localStorage so it persists across pages
      localStorage.setItem(STORAGE_KEY, selectedStation.value);
      setStationData(selectedStation);
      // Show default conditions for 1 second to indicate a change
      setCurrentConditions(defaultCurrentConditions);
      setLastUpdated('-');
      setIsSwitching(true);
      setTimeout(() => {
        const stationWeather = towerWeatherData[selectedStation.value];
        if (stationWeather) {
          setCurrentConditions(stationWeather.conditions);
          setLastUpdated(stationWeather.lastUpdated);
        }
        setIsSwitching(false);
      }, 1000);
    }
  };

  return (
    <div className={classes}>
      <div className='row'>
        <div>
          <span className='uoM'>Current Conditions:</span>{' '}
          <span className='value'>
            <Select
              startValue={stationData}
              id='station'
              options={towerList}
              required
              className='half-width lanl-form-group'
              onChange={handleChange}
            />
          </span>
        </div>
        <div className='last-updated'>
          <small>Last Updated: {lastUpdated}</small>
        </div>
      </div>
      <div className='row data'>
        {currentConditions.map(({ label, value }) => (
          <div key={`current-condition-${label}`}>
            <span className='uoM'>{label}:</span>{' '}
            <span className='value'>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

CurrentConditionsBanner.propTypes = {
  className: PropTypes.string,
};

export default CurrentConditionsBanner;
